import React, { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Col,
  Dropdown,
  Form,
  Input,
  message,
  Popconfirm,
  Row,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  Menu,
  Select,
} from 'antd';
import './styles.css';
import { CopyOutlined, DeleteOutlined, EditOutlined, LoadingOutlined, MoreOutlined } from '@ant-design/icons';
import { useTagContext } from '../../../core/components/tags/useTagContext';
import { CampaignConfig } from '../Campaigns/shared/configs/campaignConfig';
import { useCampaignRequestorContext } from '../../../core/components/campaignRequestor/useCampaignRequestorContext';

const { Title } = Typography;

export const CampaignRequestorKpiTable = ({
  form,
  handleSelectMeasurementTag,
  editable = false,
  setPreviousValues,
  // Accept reactive form values as props
  transactionsConfig,
  signUpConfig,
  sessionConfig,
  awarenessCampaign,
}) => {
  const { fetchCarts, carts } = useTagContext();
  const { kpis, setKpis, getBasicTagActivityDirect, tagActivityData, showActivityPopconfirm, setShowActivityPopconfirm, loadingBasicTagActivity } = useCampaignRequestorContext();
  
  
  // Use passed props if available, otherwise fallback to form values
  const activeAwarenessCampaign = awarenessCampaign !== undefined 
    ? awarenessCampaign 
    : form.getFieldValue('awarenessCampaign');
  const activeTransactionsConfig = transactionsConfig !== undefined 
    ? transactionsConfig 
    : form.getFieldValue('transactionsConfig');
  const activeSignUpConfig = signUpConfig !== undefined 
    ? signUpConfig 
    : form.getFieldValue('signUpConfig');
  const activeSessionConfig = sessionConfig !== undefined 
    ? sessionConfig 
    : form.getFieldValue('sessionConfig');

  const handleAwarenessCampaignChange = (e) => {
    const value = e.target.checked;
    if (value) {
      setKpis([]);
    } else {
      setKpis(CampaignConfig.kpiDefaultOptions);
    }
  };

  const processTag = ({ eventType, config, updatedKpis }) => {
    const matchedTag = updatedKpis.find((tag) => tag.key === eventType);
    const newTag = {
      ...matchedTag,
      ...config,
    };

    // Replace the matched tag with the updated tag
    const updatedTags = updatedKpis.map((tag) => (tag.key === eventType ? newTag : tag));
    return updatedTags;
  };

  const handleRemoveKPI = (record) => {
    // Don't remove the kpi instead just clear the values
    const updatedKpis = kpis.map((kpi) =>
      kpi.key === record.key
        ? {
            ...kpi,
            appId: null,
            cart: null,
            tag: null,
            tracksBasketItems: false,
            cartProvider: null,
          }
        : kpi
    );
    setKpis(updatedKpis);

    // Clear the specific hidden input value for the removed tag
    const updateObj = {};
    if (record.key === 'transactions') {
      updateObj.transactionsConfig = null;
    } else if (record.key === 'sessions') {
      updateObj.sessionConfig = null;
    } else {
      updateObj.signUpConfig = null;
    }

    form.setFieldsValue(updateObj);

    // Update previous values with current form state, only updating the removed config
    setPreviousValues({
      ...form.getFieldsValue(),
      ...updateObj,
    });
  };

  useEffect(() => {
    setKpis(activeAwarenessCampaign ? [] : CampaignConfig.kpiDefaultOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAwarenessCampaign]);

  useEffect(() => {
    let updatedKpis = [...kpis];
    if (activeTransactionsConfig) {
      updatedKpis = processTag({
        eventType: 'transactions',
        config: activeTransactionsConfig,
        updatedKpis,
      });
    }

    if (activeSignUpConfig) {
      updatedKpis = processTag({
        eventType: 'signups',
        config: {
          ...activeSignUpConfig,
          tag: {
            name: activeSignUpConfig.tag ? activeSignUpConfig.tag.name : activeSignUpConfig.name || '',
          },
        },
        updatedKpis,
      });
    }

    if (activeSessionConfig) {
      updatedKpis = processTag({
        eventType: 'sessions',
        config: activeSessionConfig,
        updatedKpis,
      });
    }

    setKpis(updatedKpis);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTransactionsConfig, activeSignUpConfig, activeSessionConfig, kpis.length]);

  useEffect(() => {
    fetchCarts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="requester-table-wrapper">
      <div className="requester-table-header">
        <Row>
          <Col span={12}>
            <Title level={5}>KPI MEASUREMENT</Title>
          </Col>
          <Col span={12}>
            <div style={{ textAlign: 'end' }}>
              <Space size="middle" align="center">
                {editable && (
                  <Form.Item
                    name="awarenessCampaign"
                    valuePropName="checked"
                    style={{ marginBottom: 0 }}
                  >
                    <Checkbox onChange={handleAwarenessCampaignChange}>
                      Awareness-Only Campaign
                    </Checkbox>
                  </Form.Item>
                )}
              </Space>
            </div>
          </Col>
        </Row>
      </div>
      {kpis.length ? (
        <Table
          key="kpi"
          columns={[
            {
              title: 'KPI',
              dataIndex: 'kpi',
              key: 'kpi',
              width: 130,
              render: (_, { tag, kpi, appId }) => (
                <Tag color={(tag && tag.name) || appId ? 'green' : undefined} key={kpi}>
                  {kpi}
                </Tag>
              ),
            },
            {
              title: 'Corresponding Tag',
              dataIndex: 'correspondingTag',
              key: 'correspondingTag',
              ellipsis: true, // Enables ellipsis for text overflow
              width: 230, // Set a fixed width for the column
              render: (_, { tag, appId, key }) => {
                if (tag && tag.name) {
                  return tag.name;
                } else if (appId || !editable) {
                  return '';
                }

                return (
                  <Button onClick={() => handleSelectMeasurementTag(key)}>
                    Select Measurement Tag
                  </Button>
                );
              },
            },
            {
              title: 'Tag ID',
              dataIndex: 'tagId',
              key: 'tagId',
              render: (_, { appId }) => {
                if (appId) {
                  return appId;
                }
                return '';
              },
            },
            {
              title: 'Basket Items',
              dataIndex: 'tracksBasketItems',
              key: 'tracksBasketItems',
              render: (_, record) => {
                if (record && record.key === 'transactions' && record.cartProvider) {
                  const cartsBasketItemsEnabled = (carts || []).filter((cart) =>
                    cart.eventsTracked.some((event) => event.value === 'basket_items')
                  );

                  let cart = record.cartProvider;
                  switch (cart) {
                    case 'DUTCHIEJS':
                      cart = 'dutchie-iframe';
                      break;
                    case 'DUTCHIE':
                      cart = 'dutchie-subdomain';
                      break;
                    case 'SHOPIFY':
                      cart = 'shopifyuniversal';
                      break;
                    default:
                      break;
                  }

                  const cartValue = cartsBasketItemsEnabled.map((cart) => cart.value);

                  const isCartEnabled = cartValue.includes(cart);
                  return (
                    <Switch
                      checked={record.tracksBasketItems}
                      disabled={!isCartEnabled}
                      onChange={(checked) => {
                        form.setFieldsValue({
                          transactionsConfig: {
                            ...transactionsConfig,
                            tracksBasketItems: checked,
                          },
                        });
                        setPreviousValues({
                          ...form.getFieldsValue(),
                          transactionsConfig: {
                            ...transactionsConfig,
                            tracksBasketItems: checked,
                          },
                        });
                      }}
                    />
                  );
                }
                return '';
              },
            },
            {
              title: 'Retail ID(s)',
              dataIndex: 'traffId',
              key: 'traffId',
              width: 200,
              render: (_, record) => {
                if (record && record.key === 'transactions' && record.cartProvider) {
                  return (
                    <Popconfirm
                      title={`There are no transactions found for this Retail ID (${tagActivityData ? tagActivityData.retailID[0].retailId : 'Loading...'}). Are you sure you want to continue?`}
                      description={`There are no transactions found for this retail ID (). Are you sure you want to continue?`}
                      open={showActivityPopconfirm}
                      onConfirm={() => setShowActivityPopconfirm(false)}
                      onCancel={() => setShowActivityPopconfirm(false)}
                      okText="Continue"
                      cancelText="Cancel"
                    >
                      <Select
                        mode="tags"
                        style={{ width: '100%' }}
                        placeholder="Please select"
                        defaultValue={(() => {
                        if (!record.traffId) return [];
                        if (Array.isArray(record.traffId)) return record.traffId;
                        if (typeof record.traffId === 'string') return record.traffId.split(',').filter(id => id);
                        return [];
                      })()}
                        loading={loadingBasicTagActivity}
                        suffixIcon={loadingBasicTagActivity ? <LoadingOutlined /> : undefined}
                        onChange={(value) => {
                          form.setFieldsValue({
                            transactionsConfig: {
                              ...transactionsConfig,
                              traffId: value.join(','),
                            },
                          });
                          setPreviousValues({
                            ...form.getFieldsValue(),
                            transactionsConfig: {
                              ...transactionsConfig,
                              traffId: value.join(','),
                            },
                          });
                        }}
                        onInputKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === 'Tab') {
                            const inputValue = e.target.value.trim();
                            if (inputValue && transactionsConfig && transactionsConfig.appId) {
                              console.log('Fetching basic tag activity for Retail ID:', inputValue);
                              console.log('Using App ID:', transactionsConfig.appId);
                              getBasicTagActivityDirect({
                                variables: {
                                  appId: Array.isArray(transactionsConfig.appId) ? transactionsConfig.appId[0] : transactionsConfig.appId,
                                  retailID: inputValue
                                }
                              });
                            }
                          }
                        }}
                        onBlur={(e) => {
                          const inputValue = e.target.value.trim();
                          if (inputValue && transactionsConfig && transactionsConfig.appId) {
                            console.log('Fetching basic tag activity for Retail ID (on blur):', inputValue);
                            console.log('Using App ID:', transactionsConfig.appId);
                            getBasicTagActivityDirect({
                              variables: {
                                appId: Array.isArray(transactionsConfig.appId) ? transactionsConfig.appId[0] : transactionsConfig.appId,
                                retailID: inputValue
                              }
                            });
                          }
                        }}
                      />
                    </Popconfirm>
                  );
                }
                return '';
              },
            },
            {
              title: '',
              dataIndex: 'actions',
              key: 'actions',
              render: (_, record) => {
                const { appId, key } = record;
                if (!appId) {
                  return '';
                }

                const handleCopyToClipBoard = () => {
                  navigator.clipboard.writeText(appId);
                  message.success('Copied to clipboard', 3);
                };

                return (
                  <Dropdown
                    trigger={['click']}
                    overlay={
                      <Menu>
                        <Menu.Item>
                          <Button
                            type="text"
                            icon={<CopyOutlined />}
                            onClick={() => handleCopyToClipBoard()}
                          >
                            Copy
                          </Button>
                        </Menu.Item>
                        {editable && (
                          <>
                            <Menu.Item>
                              <Button
                                type="text"
                                icon={<EditOutlined />}
                                onClick={() => handleSelectMeasurementTag(key)}
                              >
                                Edit
                              </Button>
                            </Menu.Item>
                            <Menu.Item>
                              <Button
                                type="text"
                                icon={<DeleteOutlined />}
                                danger
                                onClick={() => handleRemoveKPI(record)}
                              >
                                Remove
                              </Button>
                            </Menu.Item>
                          </>
                        )}
                      </Menu>
                    }
                  >
                    <MoreOutlined style={{ color: 'black' }} />
                  </Dropdown>
                );
              },
            },
          ]}
          dataSource={kpis}
          pagination={false}
          className="requester-table"
          summary={() => (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={6}>
                {' '}
                2 Tags
              </Table.Summary.Cell>
            </Table.Summary.Row>
          )}
        />
      ) : (
        <div style={{
          padding: '40px 20px',
          textAlign: 'center',
          color: '#999',
          fontSize: '14px',
          backgroundColor: '#fafafa',
          border: '1px solid #f0f0f0',
          borderRadius: '6px'
        }}>
          No measurement selected
        </div>
      )}

      {/* Create hidden input for transactionsConfig */}
      <Form.Item
        name="transactionsConfig"
        hidden
        rules={[
          ({ getFieldValue }) => ({
            validator(_, value) {
              const signUpConfig = getFieldValue('signUpConfig');
              const sessionConfig = getFieldValue('sessionConfig');
              const awarenessCampaign = getFieldValue('awarenessCampaign');

              if (signUpConfig || awarenessCampaign || sessionConfig || value) {
                return Promise.resolve();
              }
              return Promise.reject(new Error('Please select a measurement tag'));
            },
          }),
        ]}
      >
        <Input />
      </Form.Item>

      {/* Create hidden input signUpConfig */}
      <Form.Item
        name="signUpConfig"
        hidden
        rules={[
          ({ getFieldValue }) => ({
            validator(_, value) {
              const transactionsConfig = getFieldValue('transactionsConfig');
              const awarenessCampaign = getFieldValue('awarenessCampaign');
              const sessionConfig = getFieldValue('sessionConfig');
              if (transactionsConfig || awarenessCampaign || sessionConfig || value) {
                return Promise.resolve();
              }
              return Promise.reject(new Error('Please select a measurement tag'));
            },
          }),
        ]}
      >
        <Input />
      </Form.Item>

      {/* Create hidden input sessionConfig */}
      <Form.Item
        name="sessionConfig"
        hidden
        rules={[
          ({ getFieldValue }) => ({
            validator(_, value) {
              const transactionsConfig = getFieldValue('transactionsConfig');
              const signupConfig = getFieldValue('signUpConfig');
              const awarenessCampaign = getFieldValue('awarenessCampaign');
              if (transactionsConfig || awarenessCampaign || signupConfig || value) {
                return Promise.resolve();
              }
              return Promise.reject(new Error('Please select a measurement tag'));
            },
          }),
        ]}
      >
        <Input />
      </Form.Item>
    </div>
  );
};
